/**
 * Teacher layout route — /teacher/*
 * Guard: role must be 'teacher'. Redirects to /login if unauthenticated, /403 if wrong role.
 * Layout: minimal topbar (brand + hamburger on mobile) + drawer nav (sidebar) + Outlet.
 * No branch-switcher (teacher sees only assigned classes).
 */
import { createFileRoute, Outlet, Link, useNavigate, useLocation } from '@tanstack/react-router';
import {
  BookOpen,
  LogOut,
  Menu,
  Settings,
  User,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { ThemeToggle } from '@/client/components/theme-toggle';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { authClient } from '@client/lib/auth-client';

export const Route = createFileRoute('/teacher')({
  beforeLoad: async ({ navigate }) => {
    const { data: session } = await authClient.getSession();
    if (!session?.user) {
      throw navigate({ to: '/login', search: { redirect: '/teacher' }, replace: true });
    }
    const role = (session.user as { role?: string }).role ?? '';
    if (role !== 'teacher') {
      throw navigate({ to: '/403', replace: true });
    }
    return { session };
  },
  component: TeacherLayout,
});

const NAV_ITEMS = [
  { to: '/teacher', label: 'Lớp của tôi', icon: BookOpen, exact: true },
];

function TeacherLayout() {
  const navigate = useNavigate();
  const { session } = Route.useRouteContext();
  const location = useLocation();
  const user = session?.user as { name?: string; email?: string; role?: string } | undefined;
  const fullName = user?.name ?? user?.email ?? 'Giáo viên';

  const [drawerOpen, setDrawerOpen] = useState(false);

  async function handleLogout() {
    await authClient.signOut();
    void navigate({ to: '/login', replace: true });
  }

  function NavLinks({ onClick }: { onClick?: () => void }) {
    return (
      <nav className="flex flex-col gap-1 p-3">
        {NAV_ITEMS.map((item) => {
          const active = item.exact
            ? location.pathname === item.to
            : location.pathname.startsWith(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={onClick}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors min-h-[44px]
                ${active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {item.label}
            </Link>
          );
        })}
      </nav>
    );
  }

  return (
    <div className="flex min-h-screen w-full overflow-x-hidden bg-background">
      {/* Desktop sidebar — hidden on mobile */}
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-border bg-background">
        {/* Brand */}
        <div className="flex h-14 items-center px-4 border-b border-border">
          <span className="text-base font-bold text-primary">CLC LMS</span>
          <span className="ml-2 text-xs text-muted-foreground">Giáo viên</span>
        </div>
        <NavLinks />
      </aside>

      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      {/* Mobile drawer */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-background border-r border-border transform transition-transform lg:hidden
          ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex h-14 items-center justify-between px-4 border-b border-border">
          <span className="text-base font-bold text-primary">CLC LMS</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={() => setDrawerOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <NavLinks onClick={() => setDrawerOpen(false)} />
      </div>

      {/* Main content area */}
      <div className="flex flex-1 flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b border-border bg-background/95 backdrop-blur px-3 md:px-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 lg:hidden"
              onClick={() => setDrawerOpen(true)}
              aria-label="Mở menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <span className="text-sm font-semibold text-foreground lg:hidden">CLC LMS</span>
          </div>

          <div className="flex items-center gap-1">
          <ThemeToggle />
          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-2 h-9 px-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold shrink-0">
                  {fullName.charAt(0).toUpperCase()}
                </div>
                <span className="hidden sm:block text-sm truncate max-w-[120px]">{fullName}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5">
                <p className="text-xs font-medium truncate">{fullName}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <a href="/account" className="flex items-center gap-2 cursor-pointer">
                  <User className="h-4 w-4" />
                  Hồ sơ
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="/account" className="flex items-center gap-2 cursor-pointer">
                  <Settings className="h-4 w-4" />
                  Cài đặt tài khoản
                </a>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive flex items-center gap-2 cursor-pointer"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
                Đăng xuất
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
